import api from "../utils/axiosConfig";
import { BetOption, BetSelection, BetStatus } from "./game";

// ==================== Types ====================

/**
 * The ledger rows a player is ever sent.
 *
 * `CREDIT_ADJUSTMENT` is deliberately absent. An operator correcting a balance
 * writes one, but it is internal bookkeeping and the API withholds it from
 * `/me/wallet/transactions` entirely — so a branch for it here would be a branch
 * that can never run, and reads as though the type were still possible.
 *
 * The consequence is worth knowing when reading this file: on an account that
 * has been corrected, these rows do not sum to the balance. Take the balance
 * from the wallet summary rather than deriving it from the history.
 */
export type WalletEntryType =
  | "TOP_UP"
  | "WITHDRAWAL"
  | "BET_STAKE"
  | "BET_PAYOUT"
  | "BET_REFUND";

export type WalletEntryStatus =
  | "PENDING"
  | "PROCESSING"
  | "SUCCESS"
  | "FAILED"
  | "REJECTED";

export interface WalletEntry {
  type: WalletEntryType;
  amount: number;
  currency: string;
  status: WalletEntryStatus;
  created_at: string;
}

/** One row of the Bet History page — only what that page shows. */
export interface BetHistoryEntry {
  selections: BetSelection[];
  total_stake: number;
  status: BetStatus;
  payout: number;
  created_at: string;
}

export interface HistoryPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface WalletHistoryParams {
  type?: WalletEntryType;
  page?: number;
  limit?: number;
}

export interface BetHistoryParams {
  status?: BetStatus;
  page?: number;
  limit?: number;
}

export type WalletHistoryApiResponse = {
  status: number;
  system: string;
  message: string;
  data: WalletEntry[];
  pagination: HistoryPagination;
};

export type BetHistoryApiResponse = {
  status: number;
  system: string;
  message: string;
  data: BetHistoryEntry[];
  pagination: HistoryPagination;
};

// ==================== Endpoints ====================

const ENDPOINTS = {
  walletHistory: "/me/wallet/transactions",
  betHistory: "/me/bets",
};

type ApiWalletTransaction = {
  type: string;
  amountCents: number;
  status: string;
  createdAt: string;
};

type ApiBet = {
  position: number;
  betType: string;
  stakeCents: number;
  status: string;
  payoutCents: number;
  createdAt: string;
};

const toWalletEntry = (entry: ApiWalletTransaction): WalletEntry => ({
  type: entry.type.toUpperCase() as WalletEntryType,
  amount: entry.amountCents / 100,
  currency: "MYR",
  status: entry.status.toUpperCase() as WalletEntryStatus,
  created_at: entry.createdAt,
});

const toBetStatus = (status: string): BetStatus => {
  if (status === "won") return "WON";
  if (status === "lost") return "LOST";
  if (status === "voided" || status === "refunded") return "VOID";
  return "PENDING";
};

const toBetHistoryEntry = (bet: ApiBet): BetHistoryEntry => ({
  selections: [
    {
      position: bet.position,
      option: bet.betType.toUpperCase() as BetOption,
    },
  ],
  total_stake: bet.stakeCents / 100,
  status: toBetStatus(bet.status),
  payout: bet.payoutCents / 100,
  created_at: bet.createdAt,
});

export type ApiWalletHistory = {
  items: ApiWalletTransaction[];
  total: number;
  page: number;
  perPage: number;
  pages: number;
};

export type ApiBetHistory = {
  items: ApiBet[];
  total: number;
  page: number;
  perPage: number;
  pages: number;
};

export const toWalletHistoryResponse = (
  response: ApiWalletHistory
): WalletHistoryApiResponse => ({
  status: 1,
  system: "wallet",
  message: "Wallet history retrieved",
  data: response.items.map(toWalletEntry),
  pagination: {
    page: response.page,
    limit: response.perPage,
    total: response.total,
    total_pages: response.pages,
  },
});

export const toBetHistoryResponse = (
  response: ApiBetHistory
): BetHistoryApiResponse => ({
  status: 1,
  system: "game",
  message: "Bet history retrieved",
  data: response.items.map(toBetHistoryEntry),
  pagination: {
    page: response.page,
    limit: response.perPage,
    total: response.total,
    total_pages: response.pages,
  },
});

// ==================== API Functions ====================

export const getWalletHistory = async (
  params: WalletHistoryParams = {}
): Promise<WalletHistoryApiResponse> => {
  const { type, page = 1, limit = 10 } = params;

  try {
    const response = await api.get<ApiWalletHistory>(
      ENDPOINTS.walletHistory,
      { params: { type: type?.toLowerCase() ?? "all", page, perPage: limit } }
    );
    return toWalletHistoryResponse(response.data);
  } catch (error) {
    console.error("Get wallet history error:", error);
    throw error;
  }
};

/** Full-history view of the same bets `getMyBets()` serves — no second data set. */
export const getBetHistory = async (
  params: BetHistoryParams = {}
): Promise<BetHistoryApiResponse> => {
  const { status, page = 1, limit = 10 } = params;

  try {
    const response = await api.get<ApiBetHistory>(
      ENDPOINTS.betHistory,
      { params: { status: status?.toLowerCase(), page, perPage: limit } }
    );
    return toBetHistoryResponse(response.data);
  } catch (error) {
    console.error("Get bet history error:", error);
    throw error;
  }
};
